import type { IWindowService } from 'angular';
import type { StateParams } from '@uirouter/angularjs';
import type { ConfigService } from './mx-config-service';
import type { I18n } from '../../i18n/mx-internationalization';
import type { I18nService } from '../../i18n/i18n.service';
import type { MxApplication, ApplicationProvider } from '../model/application';
import { EventCodes } from '../model/event-codes';
import type { EventService } from './event-service';

/**
 * @ngdoc service
 * @name MxApplicationService
 * @module mx.shell
 *
 * @requires <a href="https://docs.angularjs.org/api/ng/service/$window" target="_blank">$window</a>
 * @requires <a href="https://github.com/angular-ui/ui-router/wiki/URL-Routing" target="_blank">$stateParams</a>
 * @requires {@ref mx.shell.Config mx.shell.Config}
 * @requires {@ref mx.internationalization mx.internationalization}
 * @requires {@ref mx.shell.ApplicationProviderService mx.shell.ApplicationProviderService}
 *
 * @description
 * A service prepares application items and provides method to get them
 */
export class MxApplicationService {
	static readonly $inject = ['$window', '$stateParams', 'mx.shell.Config', 'mx.internationalization', 'mx.shell.InternationalizationService', 'mx.shell.EventService'];

	private applicationProviderService: ApplicationProvider;
	private appItems: MxApplication[] = [];
	private currentApplication!: MxApplication;

	constructor(
		private readonly $window: IWindowService,
		private readonly $stateParams: StateParams,
		private readonly configService: ConfigService,
		private readonly i18n: I18n,
		private readonly i18nService: I18nService,
		private readonly eventService: EventService
	) {
		this.applicationProviderService = {
			load: async () => [],
		};
	}

	/**
	 * @ngdoc method
	 * @name MxApplicationService#setApplicationProvider
	 *
	 * @param {ApplicationProvider} applicationProvider - provider to load applications
	 *
	 * @description
	 * Set ApplicationProvider which is used to load applications
	 */
	public async setApplicationProvider(applicationProvider?: ApplicationProvider) {
		if (typeof applicationProvider === 'object' && typeof applicationProvider.load === 'function') {
			this.applicationProviderService = applicationProvider;
		}
		await this.reloadApplications();
	}

	/**
	 * @ngdoc method
	 * @name MxApplicationService#isEnabled
	 *
	 * @description
	 * Signals whether the Apps button is present in the Navigation panel
	 *
	 * @returns Is Apps button available. Resolves to boolean
	 */
	public isEnabled(): boolean {
		return Boolean((this.configService.get('toolbar') || {}).showAppsButton);
	}

	public async reloadApplications() {
		const appItems = await this.applicationProviderService.load(true);
		const appString = this.getAppNameFromRoute();

		this.appItems = (appItems || []).sort((a, b) => (a.position || 0) - (b.position || 0));
		const currentApp = (appString && this.appItems.find((app) => app.name && app.name.toUpperCase() === appString.toUpperCase())) || this.appItems[0];
		currentApp.isCurrent = true;
		this.currentApplication = currentApp;
	}

	public getApplications() {
		return this.appItems;
	}

	public isApplicationsButtonVisible() {
		return this.appItems.length > 1;
	}

	private getAppNameFromRoute() {
		const { $stateParams, $window } = this;
		return $stateParams.appString || ($window.location.pathname.match(/\/app-([^/^#^?]*)/) || [])[1];
	}

	public getCurrentApplication() {
		return this.currentApplication;
	}

	public setApplication(app: MxApplication, inNewWindow = false) {
		const newUrl = app.url || 'app-' + app.name;
		const basePath = this.configService.get('basePath');
		let resUrl = (basePath === '/' ? '' : basePath) + newUrl;
		const loc = location.origin;

		resUrl = resUrl.indexOf('http') === 0 ? resUrl : loc + (loc[loc.length - 1] === '/' || resUrl[0] === '/' ? '' : '/') + resUrl;

		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
			eventName: 'Application switched',
			sender: app.value,
			senderTitle: app.name,
		});

		if (inNewWindow) {
			window.open(resUrl);
		} else {
			location.href = resUrl;
		}
	}

	//just for backward compatibility
	public getCurrentApplicationSync() {
		return this.getCurrentApplication();
	}

	public async getWindowTitle(): Promise<string> {
		const { i18n, configService } = this;
		const defaultWindowTitle = i18n.get('solutionBuilderAgent.pageTitle.defaultTitle');
		const windowTitles = configService.get('defaultWindowTitles');

		if (!Array.isArray(windowTitles)) {
			return defaultWindowTitle;
		}

		const language = await this.i18nService.currentLanguage;
		if (language) {
			return (windowTitles.find((titleObject) => titleObject.lcid === language.parentLcid || titleObject.lcid === language.lcid) || {}).title || defaultWindowTitle;
		}

		return defaultWindowTitle;
	}

	public async setWindowTitle(navItemTitle: string | undefined) {
		const windowTitle = await this.getWindowTitle();
		const appName = this.getCurrentApplication()?.title ?? '';

		if (appName && navItemTitle) {
			document.title = this.i18n
				.get('solutionBuilderAgent.pageTitle.newLookCommonTitle')
				.replace('{navigation_item}', navItemTitle)
				.replace('{application_name}', appName)
				.replace('{window_title}', windowTitle);
		} else {
			document.title = windowTitle;
		}
	}
}
